/* eslint-disable no-unused-vars, no-param-reassign, react/react-in-jsx-scope, react/jsx-closing-bracket-location */
import BaseStudyApp from 'es6/controllers/apps/base-study-app';
import { AppPaths } from 'es6/core/paths';
import SentenceTrainerHelpPanel from 'es6/components/apps/settings/sentence-trainer-help-panel';
import QuizSettingsPanel from 'es6/components/apps/settings/sentence-trainer-quiz-settings-panel';
import QuizTypesPanel from 'es6/components/apps/settings/sentence-trainer-quiz-types-panel';
import { SentenceAssets } from 'es6/core/loaders';
import SentenceTrainerSequencer from 'es6/controllers/apps/sentence-trainer-sequencer';
import SentenceTrainerSummary from 'es6/components/apps/screens/sentence-trainer-summary';
import WelcomeSentenceTrainerCourse from 'es6/components/apps/screens/welcome-sentence-trainer-course';
import WelcomeSentenceTrainerStudyAll from 'es6/components/apps/screens/welcome-sentence-trainer-study-all';
import SessionProgressSteps from 'es6/components/apps/session-progress-steps';
import ScreenBanner from 'es6/components/apps/screen-banner';

export default class SentenceTrainerApp extends BaseStudyApp {

  /**
   * Takes app settings from the app launcher.
   */
  constructor(appSettings) {
    super('sentence_study', appSettings);

    // Is this a checkpoint session? Only allow on a single course study session.
    this._isCheckpoint = appSettings.isCheckpoint;
  }

  /**
   * Until the backend returns distractors, add distractors to the sentence
   * objects that will be studied.
   *
   * @override
   * @returns {Object}
   */
  _buildSessionData(...args) {
    const sessionData = super._buildSessionData(...args);

    // Construct new word entities (in the event the language study mode changed).
    sessionData.studySession.forEach(s => s.content.constructWordEntities());

    const distractorPool = sessionData.studySession
      .map(s => s.content.wordEntities)
      .reduce((a, b) => a.concat(b), [])
      .filter(w => !w.isPunctuation)
      .map(w => w.value);

    sessionData.studySession.forEach(studyNode => {
      studyNode.content.distractors = studyNode.content.getWordDistractors(distractorPool);
    });

    return sessionData;
  }

  /**
   * Creates the sequencer for sentence trainer.
   *
   * @returns {SentenceTrainerSequencer}
   */
  _createSequencer() {
    return new SentenceTrainerSequencer(this._sessionData);
  }

  _handleSequencerProgress(progress) {
    this._renderBanner({
      showPauseButton: true,
      showSettingsButton: true,
      children: (
        <SessionProgressSteps
          progress={progress}
          totalSteps={this._sequencer.totalItems} />
      ),
    });
  }

  /**
   * @override
   * @returns {Object}
   */
  _getDefaultQuizOptions() {
    return {
      listeningFillInTheBlankPartial: true,
      listeningFillInTheBlank: true,
      listeningAssembly: true,
      translationAssembly: true,
    };
  }

  /**
   * Override default session sizes.
   *
   * @returns {Number[]}
   */
  _getSessionSizes() {
    return [5, 10, 20];
  }

  /**
   * Add settings panel that are specific to sentence trainer.
   *
   * @override
   * @returns {Array}
   */
  _getSettingsPanels() {
    const panels = super._getSettingsPanels();

    panels.splice(1, 0, QuizTypesPanel, QuizSettingsPanel);
    panels.push(SentenceTrainerHelpPanel);

    return panels;
  }

  /**
   * Add study parameters that are specific to sentence trainer.
   *
   * @returns {Object}
   */
  _getStudyParameters() {
    const params = super._getStudyParameters();
    const sequenceOptions = params.sequenceOptions;
    const quizOptions = this._userSettings.apps.sentenceStudy.quizOptions;

    if (!quizOptions.listeningFillInTheBlankPartial) {
      sequenceOptions.push('no_quiz_sentence_study_listening_fill_in_the_blank_partial');
    }

    if (!quizOptions.listeningFillInTheBlank) {
      sequenceOptions.push('no_quiz_sentence_study_listening_fill_in_the_blank');
    }

    if (!quizOptions.listeningAssembly) {
      sequenceOptions.push('no_quiz_sentence_study_listening_assembly');
    }

    if (!quizOptions.translationAssembly) {
      sequenceOptions.push('no_quiz_sentence_study_translation_assembly');
    }

    return params;
  }

  /**
   * Callback for skipping checkpoint on the welcome screen.
   */
  _handleCheckpointSkip() {
    this._redirectToIKnow();
  }

  /**
   * Callback for clicking next on the summary screen.
   */
  _handleSummaryNext() {
    // If this is a checkpoint, send the user back to iKnow.
    // Otherwise, start a new session.
    if (this._isCheckpoint) {
      this._redirectToIKnow();
    } else {
      this._screenManager.show('loading');
      this._fetchData();
    }
  }

  /**
   * @override
   * @returns {Promise}
   */
  _loadSessionAssets() {
    const sentences = this._sessionData.studySession.map(s => s.content);
    return new SentenceAssets(sentences);
  }

  _redirectToIKnow() {
    this._quitApplication(AppPaths.IKNOW);
  }

  /**
   * @override
   */
  _setupScreens() {
    super._setupScreens();

    this._screenManager.addScreen('dictation_recall', Smartfm.Apps.Dictation.Screens.DictationRecall);
    this._screenManager.addScreen('dictation_quiz', Smartfm.Apps.Dictation.Screens.DictationQuiz);
    this._screenManager.addScreen('dictation_confirmation', Smartfm.Apps.Dictation.Screens.DictationConfirmation);
  }

  /**
   * @override
   */
  _showSummaryScreen(presentationResults, sessionResults) {
    const props = {
      onNext: this._handleSummaryNext.bind(this),
      presentationResults,
      sessionData: this._sessionData,
      sessionResults,
      studyAll: this._studyAll,
    };

    this._screenManager.renderComponent(SentenceTrainerSummary, props);

    // Reset the banner.
    this._renderBanner({ showSettingsButton: true });
  }

  /**
   * @override
   */
  _showWelcomeScreen() {
    const component = this._studyAll ? WelcomeSentenceTrainerStudyAll : WelcomeSentenceTrainerCourse;

    const props = {
      isCheckpoint: this._isCheckpoint,
      onCheckpointSkip: this._handleCheckpointSkip.bind(this),
      onMenu: () => this._quitApplication(),
      onSessionSizeChange: this._handleSessionChange.bind(this, 'sessionSize'),
      onGoalSourceChange: this._handleSessionChange.bind(this, 'goalSource'),
      onStart: () => this._startSession(),
      sessionConfig: this._sessionConfig,
      sessionData: this._sessionData,
    };

    this._screenManager.renderComponent(component, props);

    this._renderBanner({ showSettingsButton: true });
  }

  /**
   * @override
   * @returns {Boolean}
   */
  _updateSessionConfig() {
    return true;
  }
}
